import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, Select, Input, Button, Table, Tag, Space, message, Pagination, Tooltip } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTableQuery } from '../../../hooks/useTableQuery';
import { getWorkOrders, acceptWorkOrder, startWorkOrder, completeWorkOrder } from '../../../services/workorders';
import TimezoneAwareTime from '../../../components/TimezoneAwareTime';
import { getAllWarehouses } from '../../../services/warehouse';

const { Option } = Select;

interface Filters {
  status?: string;
  type_code?: string;
  warehouse_id?: string;
  priority?: string;
  keyword?: string;
}

const statusColor = (s: string) => (
  s === 'completed' ? 'green'
    : s === 'in_progress' ? 'blue'
    : s === 'pending' ? 'gold'
    : s === 'accepted' ? 'cyan'
    : s === 'rejected' ? 'red'
    : s === 'cancelled' ? 'red'
    : 'default'
);
const statusText = (s: string) => ({
  pending: '待领取', accepted: '已领取', in_progress: '执行中', completed: '已完成', rejected: '已驳回', cancelled: '已取消'
} as any)[s] || s || '-';

const WorkOrderListPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const taskView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get('task_view') || '').trim();
  }, [location.search]);

  const classifyWorkOrder = (row: any): 'customer_batch' | 'customer_single' | 'internal' => {
    const category = String(row?.category || '');
    const instructionId = row?.instruction_id ?? row?.instruction;
    if (category === 'internal' || !instructionId) return 'internal';
    const ctx = row?.context && typeof row.context === 'object' ? row.context : {};
    const sourceCode = String(row?.source_system_code || '').toLowerCase();
    const hasBatchHint = (
      Object.prototype.hasOwnProperty.call(ctx, 'batch_id')
      || Object.prototype.hasOwnProperty.call(ctx, 'batch_execution_task_id')
      || Object.prototype.hasOwnProperty.call(ctx, 'instruction_batch_id')
      || sourceCode.includes('batch')
    );
    return hasBatchHint ? 'customer_batch' : 'customer_single';
  };

  // 设置页面标签标题（浏览器tab英文 + 应用内标签中文）
  useEffect(() => {
    try { document.title = 'Customer Work Orders'; } catch {}
    try {
      const w: any = window as any;
      if (w?.tabsManager && typeof w.tabsManager.updateTabTitle === 'function') {
        w.tabsManager.updateTabTitle('客户工单列表');
      }
    } catch {}
  }, []);

  const { state, data, loading, actions } = useTableQuery<Filters>({
    initialState: { pagination: { current: 1, pageSize: 20, total: 0 }, filters: { status: '', type_code: '', warehouse_id: '', priority: '', keyword: '' }, sorting: { field: 'created_at', order: 'desc' } },
    urlConfig: { syncToUrl: true, urlKeys: ['pagination', 'filters', 'sorting'] },
    fetchData: async (s) => {
      const params: any = { page: s.pagination.current, page_size: s.pagination.pageSize };
      if (s.filters?.status) params.status = s.filters.status;
      if (s.filters?.type_code) params.type_code = s.filters.type_code;
      if (s.filters?.warehouse_id) params.warehouse_id = s.filters.warehouse_id;
      if (s.filters?.priority) params.priority = s.filters.priority;
      try {
        const resp = await getWorkOrders(params);
        const payload = resp || {};
        let results = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
        if (taskView) {
          results = results.filter((r: any) => classifyWorkOrder(r) === taskView);
        }
        const kw = (s.filters?.keyword || '').toString().trim().toLowerCase();
        if (kw) {
          results = results.filter((r: any) => `${r?.source_system_code || ''}`.toLowerCase().includes(kw) || `${r?.remarks || ''}`.toLowerCase().includes(kw));
        }
        const count = kw ? results.length : Number(payload?.count || results.length || 0);
        return { results, count };
      } catch (e) {
        message.error('加载失败');
        throw e;
      }
    }
  });

  // URL 参数 -> 表单回填（防止仅列表生效但筛选框未同步）
  useEffect(() => {
    try {
      form.setFieldsValue({
        status: state.filters?.status || undefined,
        priority: state.filters?.priority || undefined,
        type_code: state.filters?.type_code || undefined,
        warehouse_id: state.filters?.warehouse_id || undefined,
        keyword: state.filters?.keyword || undefined,
      });
    } catch {}
  }, [state.filters, form]);

  // 工作台快捷跳转：强制应用 URL 筛选（防止被历史标签页状态覆盖）
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const quickStatus = String(params.get('filter_status') || '').trim();
      if (quickStatus && String(state.filters?.status || '') !== quickStatus) {
        actions.updateFilters({ status: quickStatus });
        actions.updatePagination({ current: 1, pageSize: state.pagination.pageSize });
        try { form.setFieldsValue({ status: quickStatus }); } catch {}
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // 仓库选项
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  useEffect(() => {
    (async () => {
      setLoadingWarehouses(true);
      try {
        const list = await getAllWarehouses();
        setWarehouses(Array.isArray(list) ? list : []);
      } catch {
        setWarehouses([]);
      } finally {
        setLoadingWarehouses(false);
      }
    })();
  }, []);

  // 同源指令背景色计算
  const getRowColor = (r: any) => {
    const key = (r && (r.instruction != null ? String(r.instruction) : (r.source_system_code || ''))) as string;
    const palette = ['#fff7e6', '#e6f7ff', '#f6ffed', '#fff0f6', '#f9f0ff', '#e6fffb', '#f0f5ff', '#fff1f0'];
    let h = 0;
    for (let i = 0; i < key.length; i++) {
      h = ((h << 5) - h) + key.charCodeAt(i);
      h |= 0;
    }
    const color = palette[Math.abs(h) % palette.length];
    return color;
  };

  const columns = useMemo(() => {
    const base = [
      { title: '系统编码', dataIndex: 'system_code', key: 'system_code', width: 160 },
      {
        title: '类型',
        dataIndex: 'type_code',
        key: 'type_code',
        width: 90,
        render: (v: any) => {
          const txt = String(v || '').trim() || '-';
          return (
            <Tooltip title={txt}>
              <div style={{ maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {txt}
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: '工单名称',
        key: 'work_order_name',
        width: 280,
        render: (_: any, r: any) => {
          const name =
            String(r?.template_snapshot?.name || '').trim()
            || String(r?.template_name || '').trim()
            || String(r?.pricing_snapshot?.name || '').trim()
            || String(r?.type_code || '').trim()
            || '-';
          return (
            <Tooltip title={name}>
              <div style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name}
              </div>
            </Tooltip>
          );
        },
      },
      { title: '指令编码', dataIndex: 'source_system_code', key: 'source_system_code', width: 160 },
      { title: '优先级', dataIndex: 'priority', key: 'priority', width: 100, render: (v: any) => <Tag color={v==='super'?'red':(v==='high'?'orange':'default')}>{v==='super'?'超级优先':v==='high'?'一般优先':'普通'}</Tag> },
      { title: '状态', dataIndex: 'status', key: 'status', width: 110, render: (v: any) => <Tag color={statusColor(String(v))}>{statusText(String(v))}</Tag> },
      { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160, render: (v: any) => v ? <TimezoneAwareTime time={v} format="YYYY-MM-DD HH:mm:ss" showTimezone={false} /> : '-' },
      { title: '操作', key: 'ops', width: 240, render: (_: any, r: any) => {
      const on = async (kind: 'accept'|'start'|'complete') => {
        try {
          if (kind==='accept') await acceptWorkOrder(r.id);
          if (kind==='start') await startWorkOrder(r.id);
          if (kind==='complete') await completeWorkOrder(r.id);
          message.success('已提交');
          actions.refresh();
        } catch (e: any) {
          message.error(e?.response?.data?.detail || '操作失败');
        }
      };
      return (
        <Space size={4}>
          {/* 工单详情页已移除：避免死链 */}
          {r.status === 'pending' && <Button size="small" onClick={() => on('accept')}>领取</Button>}
          {r.status === 'accepted' && <Button size="small" onClick={() => on('start')}>开始</Button>}
          {r.status === 'in_progress' && <Button size="small" onClick={() => on('complete')}>完成</Button>}
        </Space>
      );
      } }
    ] as any[];
    // 为每列增加 onCell 以覆盖固定列/单独表格的背景色
    return base.map((col: any) => ({
      ...col,
      onCell: (record: any) => ({ style: { backgroundColor: getRowColor(record) } })
    }));
  }, [actions, navigate]);

  return (
    <div className="list-page-container">
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>客户工单列表</div>
          <Button icon={<ReloadOutlined />} onClick={() => actions.refresh()}>刷新</Button>
        </div>
        {taskView ? (
          <div style={{ marginBottom: 12, color: '#1677ff', fontSize: 13 }}>
            已应用工作台筛选：{taskView === 'customer_batch' ? '客户指令-批次执行' : taskView === 'customer_single' ? '客户指令-单指令执行' : '仓内工单'}
          </div>
        ) : null}
        <Form
          form={form}
          layout="horizontal"
          onFinish={() => actions.updateFilters(form.getFieldsValue())}
          onValuesChange={(_changed, values) => actions.updateFilters(values)}
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item name="status" label="状态">
                <Select allowClear placeholder="选择状态">
                  <Option value="pending">待领取</Option>
                  <Option value="accepted">已领取</Option>
                  <Option value="in_progress">执行中</Option>
                  <Option value="completed">已完成</Option>
                  <Option value="rejected">已驳回</Option>
                  <Option value="cancelled">已取消</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item name="priority" label="优先级">
                <Select allowClear placeholder="选择优先级">
                  <Option value="normal">普通</Option>
                  <Option value="high">一般优先</Option>
                  <Option value="super">超级优先</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item name="type_code" label="类型">
                <Input placeholder="例如 RE-LABEL/GENERIC" allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item name="warehouse_id" label="仓库">
                <Select
                  allowClear
                  placeholder="选择仓库"
                  loading={loadingWarehouses}
                  showSearch
                  optionFilterProp="label"
                  options={(warehouses || []).map((w:any) => ({
                    value: String(w.id),
                    label: `${w.name || ''}${(w.code || w.warehouse_code) ? ` · ${w.code || w.warehouse_code}` : ''}`.trim()
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="keyword" label="关键字">
                <Input allowClear placeholder="指令编码/备注" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item>
                <Space>
                  <Button type="primary" icon={<SearchOutlined />} htmlType="submit">搜索</Button>
                  <Button icon={<ReloadOutlined />} onClick={() => { form.resetFields(); actions.reset(); }}>重置</Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Table
          rowKey={(r:any)=> String(r.id)}
          loading={loading}
          dataSource={data}
          columns={columns as any}
          pagination={false}
          scroll={{ x: 1000 }}
          style={{ marginTop: 12 }}
          onRow={(r:any) => {
            const color = getRowColor(r);
            return { style: { backgroundColor: color } } as any;
          }}
        />
        <div className="flex justify-end items-center" style={{ marginTop: 12 }}>
          <Pagination
            current={state.pagination.current}
            pageSize={state.pagination.pageSize}
            total={state.pagination.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`}
            pageSizeOptions={[10,20,50,100] as any}
            onChange={(p, ps) => actions.updatePagination({ current: p, pageSize: ps })}
          />
        </div>
      </Card>
    </div>
  );
};

export default WorkOrderListPage;


